import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ArrowLeft, Lock, RotateCcw } from 'lucide-react';
import { ADMIN_PERM_KEYS, ADMIN_PERM_META, adminLevel, NON_GRANTABLE, presetLevel, ROLE_BLURB, TEAM_ROLES, type AdminPerm, type AdminPermOverrides } from '@dd/shared';
import { Banner, Button, Input, Switch, TextArea, toastOk } from '@dd/ui';
import { api, errText } from '@/lib/api';
import { useGet } from '@/lib/hooks';
import { cn } from '@dd/ui';
import { Chip, ErrorBox, Loading, PageHead, Panel, RoleBadge, ROLE_LABEL } from '@/components/ui';
import type { AdminRow } from './Admins';

const GROUPS = ['Customers', 'Money', 'Support', 'Content & system', 'Admin'] as const;
const LEVEL_NOTE: Record<string, string> = { content_only: 'content figures only', billing_only: 'billing tickets only', up_to_5000: 'up to ৳5,000', verify_submit: 'verify & submit (Super Admin approves)', prepare: 'prepare (Super Admin approves)', approve: 'approves', all: 'everything' };

/** Create an admin or change one: pick the role, then switch single permissions on or off. The Super Admin is the only person who can open this page. */
export function AdminEditor() {
  const { id } = useParams() as { id?: string };
  const isNew = !id;
  const nav = useNavigate();
  const list = useGet<{ items: AdminRow[] }>('/admins', undefined, !isNew);
  const existing = list.data?.items.find((a) => a.id === id);
  const [name, setName] = useState(''); const [email, setEmail] = useState(''); const [pw, setPw] = useState('');
  const [role, setRole] = useState<(typeof TEAM_ROLES)[number]>('support');
  const [ov, setOv] = useState<AdminPermOverrides>({});
  const [review, setReview] = useState(false); const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (existing) { setName(existing.name); setEmail(existing.email); setRole(existing.role as never); setOv(existing.perms ?? {}); setReview(existing.canReview); } }, [existing]);

  const level = (p: AdminPerm) => adminLevel(role, p, ov);
  const isOn = (p: AdminPerm) => level(p) !== 0;
  const toggle = (p: AdminPerm, v: boolean) => setOv((o) => {
    const n = { ...o };
    if (v === (presetLevel(role, p) !== 0)) delete n[p]; else n[p] = v;
    return n;
  });
  const changed = useMemo(() => ADMIN_PERM_KEYS.filter((k) => ov[k] !== undefined && ov[k] !== (presetLevel(role, k) !== 0)), [ov, role]);
  const total = ADMIN_PERM_KEYS.filter(isOn).length;
  const valid = name.trim().length >= 2 && (!isNew || (/^\S+@\S+\.\S+$/.test(email) && pw.length >= 12)) && reason.trim().length >= 5;

  const save = async () => {
    setBusy(true); setErr(null);
    const perms = Object.fromEntries(changed.map((k) => [k, ov[k]]));
    try {
      if (isNew) await api.post('/admins', { name: name.trim(), email: email.trim(), role, canReview: review, perms, password: pw, reason: reason.trim() });
      else await api.patch(`/admins/${id}`, { name: name.trim(), role, canReview: review, perms, reason: reason.trim() });
      toastOk(isNew ? 'Admin created — they enrol 2-step at first sign-in' : 'Access updated');
      nav('/admins');
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  if (!isNew && list.isLoading) return <Loading />;
  if (!isNew && (list.error || !existing)) return <ErrorBox error={list.error ?? new Error('Admin not found')} />;
  return (
    <>
      <Link to="/admins" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-brand"><ArrowLeft size={16} />Admins & roles</Link>
      <PageHead title={isNew ? 'Add an admin' : `Access for ${existing!.name}`} sub={isNew ? 'They sign in with the email and password below and must set up two-step verification at their first login.' : 'Role sets the starting permissions; the switches below change single permissions for this person only.'} />
      {err && <Banner tone="bad" className="mb-4">{err}</Banner>}
      <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
        <div className="space-y-5">
          <Panel title="Person">
            <div className="space-y-3">
              <Input label="Full name" value={name} onChange={(e) => setName(e.target.value)} />
              <Input label="Login email" type="email" value={email} disabled={!isNew} onChange={(e) => setEmail(e.target.value)} hint={isNew ? undefined : 'The email cannot be changed. Remove the admin and add again if needed.'} />
              {isNew && <Input label="First password (min 12 characters)" type="text" autoComplete="off" value={pw} onChange={(e) => setPw(e.target.value)} hint="Share it through a different channel than the email." error={pw && pw.length < 12 ? 'At least 12 characters' : null} />}
              <Switch checked={review} onChange={setReview} label="Can decide approval requests" hint="Approve or reject other admins’ large refunds, long gifts, transfers" />
            </div>
          </Panel>
          <Panel title="Role">
            <div className="space-y-2">
              {TEAM_ROLES.map((r) => (
                <button key={r} type="button" onClick={() => setRole(r)} className={cn('w-full rounded-xl border p-3 text-left transition', role === r ? 'border-brand bg-brand-50 ring-1 ring-brand/20' : 'border-line hover:bg-neutral-50')}>
                  <div className="flex items-center justify-between"><RoleBadge role={r} />{role === r && <Chip tone="brand">selected</Chip>}</div>
                  <div className="mt-1.5 text-xs text-ink-2">{ROLE_BLURB[r]}</div>
                </button>
              ))}
            </div>
          </Panel>
        </div>

        <Panel title={<span className="flex items-center gap-2">Permissions <Chip>{total} on</Chip>{changed.length > 0 && <Chip tone="warn">{changed.length} changed from {ROLE_LABEL[role]}</Chip>}</span>}
          right={changed.length > 0 ? <Button size="sm" variant="ghost" icon={<RotateCcw size={14} />} onClick={() => setOv({})}>Reset to role</Button> : undefined}>
          <div className="space-y-6">
            {GROUPS.map((g) => (
              <section key={g}>
                <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wider text-ink-3">{g}</h3>
                <div className="divide-y divide-line/70 rounded-xl border border-line/80">
                  {ADMIN_PERM_KEYS.filter((k) => ADMIN_PERM_META[k].group === g).map((k) => {
                    const m = ADMIN_PERM_META[k];
                    const locked = NON_GRANTABLE.includes(k);
                    const diff = ov[k] !== undefined && ov[k] !== (presetLevel(role, k) !== 0);
                    const lv = level(k);
                    return (
                      <div key={k} className={cn('flex items-center gap-3 px-4 py-2.5', diff && 'bg-warn-bg/40')}>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">{m.label}{m.sensitive && <Chip tone="bad">sensitive</Chip>}{diff && <Chip tone="warn">{ov[k] ? 'added' : 'removed'}</Chip>}{lv !== 0 && lv !== 1 && <Chip>{LEVEL_NOTE[String(lv)] ?? String(lv)}</Chip>}</div>
                          <div className="text-xs text-ink-3">{m.hint}</div>
                        </div>
                        {locked ? <span className="flex items-center gap-1 text-xs font-semibold text-ink-3"><Lock size={13} />Super Admin only</span> : <Switch checked={isOn(k)} onChange={(v) => toggle(k, v)} />}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
          <div className="mt-6 space-y-3 border-t border-line pt-5">
            {changed.some((k) => ADMIN_PERM_META[k].sensitive && ov[k]) && <Banner tone="warn">You are switching on sensitive permissions. Everything this person does with them is written to the audit log under their name.</Banner>}
            <TextArea label="Reason (audited, min 5 characters)" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
            <div className="flex justify-end gap-2"><Link to="/admins"><Button variant="outline">Cancel</Button></Link><Button loading={busy} disabled={!valid} onClick={() => void save()}>{isNew ? 'Create admin' : 'Save access'}</Button></div>
          </div>
        </Panel>
      </div>
    </>
  );
}
